import { HTMLAttributes } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { wacToken } from "@/libraries/design/foundations/index.ts";
import { Icon } from "@/libraries/wpl/foundations/src/icon";

export type SimpleCellIcon = "checkmark" | "x";

export interface GenericTableSimpleCellProps
    extends HTMLAttributes<HTMLTableCellElement> {
    text?: string;
    icon?: SimpleCellIcon;
}

const cellCss = css({
    "fontWeight": 400,
    "textAlign": "left",
    "color": wacToken("color.primary.black"),
    "fontSize": "14px",
    "display": "table-cell",
    "verticalAlign": "top",
    "padding": "14px",
    "borderInlineEnd": `1px solid ${wacToken("color.neutral.light400")}`,
    ":last-of-type": {
        borderInlineEnd: "none"
    },
    [media.below.sm]: {
        display: "none"
    }
});

const cellContentCss = css({
    display: "flex",
    alignItems: "center",
    gap: "8px"
});

const iconCircleCss = css({
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    height: "24px",
    width: "24px"
});

const checkmarkCircleCss = css({
    backgroundColor: wacToken("color.extended.lime700")
});

const crossCircleCss = css({
    backgroundColor: wacToken("color.secondary.red")
});

export const GenericTableSimpleCell = ({
    text,
    icon,
    ...props
}: GenericTableSimpleCellProps) => {
    const iconElement =
        icon === "checkmark" ? (
            <span css={[iconCircleCss, checkmarkCircleCss]}>
                <Icon
                    name="featurecheckmark"
                    size="xs"
                    color={wacToken("color.neutral.light0")}
                />
            </span>
        ) : icon === "x" ? (
            <span css={[iconCircleCss, crossCircleCss]}>
                <div css={css({ transform: "scale(0.7)" })}>
                    <Icon
                        name="cross"
                        size="xs"
                        color={wacToken("color.neutral.light0")}
                    />
                </div>
            </span>
        ) : null;

    const hasIconAndText = !!iconElement && !!text;
    return (
        <td {...props} css={cellCss}>
            {hasIconAndText ? (
                <span css={cellContentCss}>
                    {iconElement}
                    {text}
                </span>
            ) : (
                <>
                    {iconElement}
                    {text ?? null}
                </>
            )}
        </td>
    );
};
